/** @format */

import registry from './dirRegistry';

class DirSyncManager {
  constructor({ store, axios, refreshDir }) {
    this.store = store;
    this.axios = axios;
    this.refreshDir = refreshDir;

    this.dirtyDirs = new Set();
    this.pending = new Set();
    this.timer = null;

    this.isActive = !document.hidden;

    this.TAB_ID = Date.now() + '_' + Math.random();

    this.channel = new BroadcastChannel('dir-sync');

    this.HEARTBEAT_INTERVAL = 2000;
    this.LEADER_TTL = 5000;

    this.init();
  }

  // =====================
  // INIT
  // =====================

  init() {
    document.addEventListener('visibilitychange', () => {
      this.isActive = !document.hidden;

      if (this.isActive) {
        this.becomeLeader();
        this.processDirty();
      }
    });

    window.addEventListener('focus', () => {
      this.becomeLeader();
    });

    this.channel.onmessage = (event) => {
      const msg = event.data;

      if (!msg?.type) return;

      if (msg.type === 'dir_patch') {
        this._applyPatch(msg);
        return;
      }

      if (msg.type === 'dir_cache_request') {
        this.sendCacheSnapshot(msg);
        return;
      }

      if (msg.type === 'dir_cache_response') {
        this.restoreCache(msg);
        return;
      }

      this._handle(msg);
    };

    this.startHeartbeat();

    this.becomeLeader();

    // новая вкладка пытается забрать кэш
    setTimeout(() => {
      this.requestCache();
    }, 500);
  }

  // =====================
  // LEADER
  // =====================

  becomeLeader() {
    localStorage.setItem(
      'dir_sync_leader',
      JSON.stringify({
        id: this.TAB_ID,
        ts: Date.now(),
      }),
    );
  }

  getLeader() {
    try {
      return JSON.parse(localStorage.getItem('dir_sync_leader'));
    } catch (e) {
      return null;
    }
  }

  isLeaderAlive() {
    const leader = this.getLeader();

    if (!leader) return false;

    return Date.now() - leader.ts < this.LEADER_TTL;
  }

  isLeader() {
    const leader = this.getLeader();

    if (!leader) return false;

    if (!this.isLeaderAlive()) {
      this.becomeLeader();
      return true;
    }

    return leader.id === this.TAB_ID;
  }

  startHeartbeat() {
    this.heartbeat = setInterval(() => {
      if (this.isActive && this.isLeader()) {
        this.becomeLeader();
      }
    }, this.HEARTBEAT_INTERVAL);
  }

  // =====================
  // CACHE SHARING
  // =====================

  requestCache() {
    this.channel.postMessage({
      type: 'dir_cache_request',
      from: this.TAB_ID,
    });
  }

  sendCacheSnapshot(msg) {
    if (!this.isLeader()) return;

    const snapshot = {};

    Object.keys(this.store.state.dirs.data).forEach((key) => {
      if (this.store.state.dirs.loaded[key]) {
        snapshot[key] = this.store.state.dirs.data[key];
      }
    });

    this.channel.postMessage({
      type: 'dir_cache_response',
      to: msg.from,
      data: snapshot,
    });
  }

  restoreCache(msg) {
    if (msg.to !== this.TAB_ID) return;

    const data = msg.data || {};

    Object.keys(data).forEach((dir) => {
      console.log('restore dir', dir, data[dir]);
      this.store.commit('dirs/SET_DIR', {
        name: dir,
        items: data[dir],
      });
    });

    console.log('[DIR CACHE RESTORED]', Object.keys(data));
  }

  // =====================
  // PUBLIC ENTRY
  // =====================

  onSocketMessage(msg) {
    if (!msg?.type) return;

    switch (msg.type) {
      case 'dir_update':
        this.channel.postMessage(msg);
        this._handle(msg);
        break;

      case 'dir_patch':
        this.channel.postMessage(msg);
        this._applyPatch(msg);
        break;
    }
  }

  // =====================
  // PATCH
  // =====================

  broadcastPatch(dir, item) {
    this.channel.postMessage({
      type: 'dir_patch',
      dir,
      item,
    });
  }

  _applyPatch(msg) {
    const { dir, item } = msg;

    const isUsed = this.store.getters['dirs/isUsed'](dir);

    if (!isUsed) return;

    // только вторичные вкладки
    if (!this.isLeader()) {
      this.store.commit('dirs/UPDATE_ITEM', {
        name: dir,
        item,
      });
    }
  }

  // =====================
  // UPDATE EVENTS
  // =====================

  _handle(msg) {
    const { dir, id } = msg;

    this.pending.add(JSON.stringify({ dir, id }));

    clearTimeout(this.timer);

    this.timer = setTimeout(() => {
      const updates = Array.from(this.pending).map((v) => JSON.parse(v));

      this.pending.clear();

      updates.forEach(({ dir, id }) => {
        const isUsed = this.store.getters['dirs/isUsed'](dir);

        if (!isUsed) return;

        if (this.isActive && this.isLeader()) {
          if (id) {
            this._updateOne(dir, id);
          } else {
            this._safeRefresh(dir);
          }
        } else {
          this.dirtyDirs.add(
            JSON.stringify({
              dir,
              id,
            }),
          );
        }
      });
    }, 300);
  }

  async _updateOne(dir, id) {
    try {
      // fallback если нет byId
      if (!registry?.[dir]?.byId) {
        console.log('[DIR fallback refresh]', dir);

        await this._safeRefresh(dir);

        return;
      }

      await this.store.dispatch('dirs/updateById', {
        name: dir,
        id,
        axios: this.axios,
      });

      const list = this.store.getters['dirs/get'](dir);

      const item = list.find((x) => (x.id || x.value) === id);

      if (item) {
        this.broadcastPatch(dir, item);
      }
    } catch (e) {
      console.error('[DIR PATCH ERROR]', dir, id, e);

      await this._safeRefresh(dir);
    }
  }

  async processDirty() {
    if (!this.dirtyDirs.size) return;

    if (!this.isLeader()) return;

    const updates = Array.from(this.dirtyDirs).map((v) => JSON.parse(v));

    this.dirtyDirs.clear();

    await Promise.all(
      updates.map(({ dir, id }) => {
        if (id) {
          return this._updateOne(dir, id);
        }

        return this._safeRefresh(dir);
      }),
    );
  }

  async _safeRefresh(dir) {
    if (typeof dir !== 'string') return;

    try {
      await this.refreshDir(dir);
    } catch (e) {
      console.error('[DIR SYNC ERROR]', dir, e);
    }
  }
}

export default DirSyncManager;
